import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const NewConversationModal = ({ show, onHide }) => {
  const navigate = useNavigate();

  const loginUserId = Number(localStorage.getItem("loginUserId"));
  const userId = Number(localStorage.getItem("userId"));
  const loginUserType = "USER";
  const loginUserRole = localStorage.getItem("userName"); // ✅ IMPORTANT

  const isAdmin = loginUserRole === "SMS_ADMIN";

  const [activeTab, setActiveTab] = useState("STUDENT");
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    if (!show) return;

    const load = async () => {
      try {
        // ADMIN: load both
        if (isAdmin) {
          const [uRes, sRes] = await Promise.all([
            api.get("/getUserList"),
            api.get("/getStudentList"),
          ]);

          setUsers(
            (uRes.data || []).map((u) => ({
              value: u.userRegId,
              label: u.userRole ? `${u.userName} (${u.userRole})` : u.userName,
              type: "USER",
            })),
          );

          setStudents(
            (sRes.data || []).map((s) => ({
              value: s.studentId,
              label: s.studentUniqueId
                ? `${s.studentName} (${s.studentUniqueId})`
                : s.studentName,
              type: "STUDENT",
            })),
          );
        }
        // NON-ADMIN: students by teacher
        else {
          const sRes = await api.get(`/getStudentListByTeacherId/${userId}`);

          setStudents(
            (sRes.data || []).map((s) => ({
              value: s.studentId,
              label: s.studentUniqueId
                ? `${s.studentName} (${s.studentUniqueId})`
                : s.studentName,
              type: "STUDENT",
            })),
          );
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load recipients");
      }
    };

    setSelected(null);
    setActiveTab(isAdmin ? "USER" : "STUDENT");
    load();
  }, [show, isAdmin, loginUserId]);

  /* ---------------- CREATE CONVERSATION ---------------- */
  const handleCreate = async () => {
    if (!selected) {
      toast.warning("Please select a recipient");
      return;
    }

    try {
      setLoading(true);

      const res = await api.post("/createConversation", {
        createdByType: loginUserType,
        createdById: loginUserId,
        type: "PRIVATE",
        title: null,
        participants: [
          { participantId: loginUserId, participantType: loginUserType },
          { participantId: selected.value, participantType: selected.type },
        ],
      });

      toast.success("Conversation created");
      onHide();

      navigate(`/messaging/view/${res.data.conversationId}`, {
        state: {
          participants: [
            {
              userId: loginUserId,
              userType: loginUserType,
            },
            {
              userId: selected.value,
              userType: selected.type,
              userName: selected.label,
            },
          ],
        },
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to create conversation");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- REACT SELECT STYLE ---------------- */
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: 48,
      borderRadius: 8,
      boxShadow: "none",
    }),
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      {/* HEADER */}
      <Modal.Header closeButton>
        <Modal.Title>New Message</Modal.Title>
      </Modal.Header>

      {/* BODY */}
      <Modal.Body>
        {/* TABS (ADMIN ONLY) */}
        {isAdmin && (
          <div className="d-flex justify-content-center mb-4">
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                borderRadius: 10,
                padding: 4,
              }}
            >
              {["USER", "STUDENT"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setSelected(null);
                  }}
                  style={{
                    border: "none",
                    padding: "8px 24px",
                    borderRadius: 8,
                    background: activeTab === tab ? "#ffffff" : "transparent",
                    fontWeight: 600,
                  }}
                >
                  {tab === "USER" ? "User" : "Student"}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DROPDOWN */}
        <Select
          placeholder={
            activeTab === "USER"
              ? "Search & select user..."
              : "Search & select student..."
          }
          options={activeTab === "USER" ? users : students}
          value={selected}
          onChange={setSelected}
          isSearchable
          menuPortalTarget={document.body}
          styles={selectStyles}
        />
      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>

        <Button
          variant="success"
          onClick={handleCreate}
          disabled={!selected || loading}
        >
          {loading ? "Starting..." : "Start Chat"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewConversationModal;
